import JxtFloorPlan from '../JxtFloorPlan';
import { IJxtFloorPlanPictos, TFloorResourceMarkersWithResourceId } from './types';
import { FloorPlanPictoTypeEnum, IUsePictoGeneratorOptions, useFloorMatrix, usePictoGenerator } from '../../hooks';
import { useFetchFloorResourceMarkers } from '@jooxter/floors';
import { useFetchUser } from '@jooxter/users';
import { useTranslation } from 'react-i18next';
import { getBookingInformation } from '../../utils';
import { IResourcePictoData } from '../../core';
import { useBookingCompressedToIFloorPlanBookingConverter } from '../../core/converters/useBookingCompressedToFloorPlanBookingConverter';
import { useResourceFloorDTOToIFloorPlanResourceConverter } from '../../core/converters/useResourceFloorDTOToFloorPlanResourceConverter';
import { useMemo, useState } from 'react';
import { useResourceTypeRepository } from '@jooxter/resource-types';
import { getLoader } from './getLoader';
import { IPictoConfig } from '../JxtFloorPlan/types';

/**
 * NOTE: This component requires adapter hooks from @jooxter/core:
 * - useBookingCompressedToIFloorPlanBookingAdapter
 * - useResourceFloorDTOToIFloorPlanResourceAdapter
 *
 * These adapters must be applied before passing data to this component.
 * See the backward compatibility wrapper in @jooxter/core for usage example.
 */
const JxtFloorPlanPictos = ({
  resources,
  width,
  height,
  floorPlan,
  type,
  bookings,
  onClick,
  heatmapData,
  focusResourceId,
}: IJxtFloorPlanPictos) => {
  // For now, we pass the resources directly. In the future, adapters may be moved here
  // or handled in the layer-based architecture
  const { floorResourceMarkers } = useFetchFloorResourceMarkers(floorPlan.id);
  const { convert: convertBookings } = useBookingCompressedToIFloorPlanBookingConverter(bookings);
  const { convert: convertResources } = useResourceFloorDTOToIFloorPlanResourceConverter(resources, bookings);
  const convertedResources = useMemo(() => convertResources(), [convertResources]);
  const convertedBookings = useMemo(() => convertBookings(), [convertBookings]);
  const { user } = useFetchUser();
  const { t } = useTranslation();
  const { repository } = useResourceTypeRepository();
  const { matrix } = useFloorMatrix(floorPlan.matrix);
  const markers = floorResourceMarkers?.filter(
    (frm): frm is TFloorResourceMarkersWithResourceId => !!(frm.resource?.id && frm.coordinate?.x && frm.coordinate?.y)
  );
  const options: IUsePictoGeneratorOptions = {
    floorPlanImage: floorPlan?.image,
    scalePixelsPerMeter: floorPlan.scalePixelsPerMeter,
    mapIconScale: floorPlan.mapIconScale,
    matrix,
    apiMarkers: markers,
    dimensions: { width, height },
  };
  const { generator, backgroundImage, isReady } = usePictoGenerator(options);
  const [prioritizedPictoId, setPrioritizedPictoId] = useState<number | null>(null);
  const heatmapByResourceId = useMemo(() => {
    const entries = (heatmapData ?? []).flatMap((h) =>
      h.resourceId === undefined ? [] : [[h.resourceId, h.value] as const]
    );
    return new Map<number, number | undefined>(entries);
  }, [heatmapData]);

  if (!user || !isReady) {
    return getLoader();
  }

  const resourceHeatmapPictoData = new Map(
    convertedResources
      .map((r) => {
        return {
          id: r.id,
          name: r.name,
          pictogram: r.state?.pictogram,
          showMapIcon: true,
          isFavorite: r.favorite,
          status: r.state?.status,
          capacity: r.capacity,
          occupantsCount: r.state?.occupantsCount,
          heatmapIntensity: heatmapByResourceId.get(r.id),
        };
      })
      .filter((r) => r.pictogram && r.isFavorite !== undefined)
      .map((r) => [r.id, r as IResourcePictoData])
  );

  const resourceStatePictoData = new Map(
    convertedResources
      .map((r) => ({
        id: r.id,
        name: r.name,
        pictogram: r.state?.pictogram,
        showMapIcon: r.resourceType?.showMapIcon,
        isFavorite: r.favorite,
        status: r.state?.status,
        capacity: r.capacity,
        occupantsCount: r.state?.occupantsCount,
        subtitle: getBookingInformation(
          user,
          t,
          r.resourceType.allowOverlap,
          !r.bookable,
          r.capacity,
          r.bookings,
          r.state?.occupantsCount
        ),
      }))
      .filter((r): r is IResourcePictoData => 'pictogram' in r && 'isFavorite' in r)
      .map((r) => [r.id, { ...r }])
  );
  const userPictoData = new Map(
    convertedBookings.map((b) => [
      b.resource.id,
      {
        firstName: b.organizer.firstname,
        lastName: b.organizer.lastname,
        pictureUrl: b.organizer.picture,
      },
    ])
  );
  const resourceIconSizes = new Map(resources.map((r) => [r.id, repository.findById(r.resourceTypeId)?.mapIconSize]));
  const getPictos = () => {
    switch (type) {
      case FloorPlanPictoTypeEnum.ResourceHeatmap:
        return generator?.generateResourceHeatmapPictos(resources, resourceHeatmapPictoData, resourceIconSizes);
      case FloorPlanPictoTypeEnum.ResourceState:
        return generator?.generateResourcePictos(resources, resourceStatePictoData, resourceIconSizes);
      case FloorPlanPictoTypeEnum.Avatar:
        return generator?.generateUserPictos(userPictoData, convertedBookings);
      case FloorPlanPictoTypeEnum.Pin:
        return generator?.generateBookingPictos(convertedBookings);
      default:
        console.warn(`Unsupported picto type: ${type}, skipping picto generation.`);
        break;
    }
  };
  const pictos = getPictos();
  const prioritizedPictos: IPictoConfig[] =
    pictos?.map((picto) => {
      if (picto.id === prioritizedPictoId) {
        picto.priority = 1;
      } else {
        picto.priority = 2;
      }

      return picto;
    }) ?? [];

  return (
    <JxtFloorPlan
      changePictoPriority={(p) => setPrioritizedPictoId(p.id)}
      backgroundImage={backgroundImage}
      pictos={prioritizedPictos}
      width={width}
      height={height}
      onClick={onClick}
      focusPictoId={focusResourceId}
    />
  );
};

export default JxtFloorPlanPictos;
